import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import dayjs from 'dayjs'
import { Timestamp } from 'firebase/firestore'
import { BsBell } from 'react-icons/bs'
import { IoClose } from 'react-icons/io5'

import { Button } from '..'
import { useUser } from '../../context'
import { useNotifications } from '../../hooks/useNotifications'

import {
  Badge,
  BellButton,
  EmptyState,
  HeaderRow,
  Item,
  Items,
  Overlay,
  Panel,
  PanelFooter,
  PanelTitle,
  Popover,
  Row,
  SubTitle,
} from './styles'

type Props = {
  maxItems?: number
  pageSize?: number
  onOpenChallenge?: (challengeId: string) => void
}

const HeaderNotification = ({
  maxItems = 8,
  pageSize = 50,
  onOpenChallenge,
}: Props) => {
  const { user, userRole } = useUser()
  const { feed, unreadCount, lastSeen, markAllAsSeen } = useNotifications({
    userId: user?.uid,
    userRole,
    pageSize,
  })

  const [open, setOpen] = useState(false)
  const popoverRef = useRef<HTMLDivElement | null>(null)

  const close = useCallback(() => setOpen(false), [])
  const toggle = useCallback(() => setOpen((v) => !v), [])

  // cerrar al click afuera
  useEffect(() => {
    if (!open) return

    const onClick = (e: MouseEvent) => {
      if (!popoverRef.current) return
      if (!popoverRef.current.contains(e.target as Node)) setOpen(false)
    }

    document.addEventListener('mousedown', onClick)
    return () => document.removeEventListener('mousedown', onClick)
  }, [open])

  // marcar como visto al abrir (ahora NO vacía la lista porque usamos feed)
  useEffect(() => {
    if (open && unreadCount > 0) {
      markAllAsSeen()
    }
  }, [open, unreadCount, markAllAsSeen])

  const visibleItems = useMemo(() => feed.slice(0, maxItems), [feed, maxItems])

  const isUnread = useCallback(
    (createdAt?: Timestamp) => {
      if (!createdAt) return false
      if (!lastSeen) return true
      return createdAt.toMillis() > lastSeen.toMillis()
    },
    [lastSeen],
  )

  // solo students
  if (userRole !== 'student') return null

  return (
    <Popover ref={popoverRef}>
      <BellButton
        type="button"
        aria-label="Notificaciones"
        aria-expanded={open}
        onClick={toggle}
      >
        <BsBell />

        {unreadCount > 0 && (
          <Badge>{unreadCount > 99 ? '99+' : unreadCount}</Badge>
        )}
      </BellButton>

      {open && (
        <>
          <Overlay onClick={close} />
          <Panel>
            <HeaderRow>
              <div>
                <PanelTitle>Notificaciones</PanelTitle>
              </div>
              <Button className="close" onClick={close} label={<IoClose />} />
            </HeaderRow>

            {visibleItems.length === 0 ? (
              <EmptyState>No hay notificaciones por ahora.</EmptyState>
            ) : (
              <Items>
                {visibleItems.map((n) => (
                  <Item
                    key={n.id}
                    $unread={isUnread(n.createdAt)}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (onOpenChallenge && n.documentId)
                        onOpenChallenge(n.documentId)
                      close()
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        if (onOpenChallenge && n.documentId)
                          onOpenChallenge(n.documentId)
                        close()
                      }
                    }}
                  >
                    <Row>
                      <div className="title">{n.title}</div>
                      <div className="date">
                        {n.createdAt
                          ? dayjs(n.createdAt.toDate()).format('DD/MM')
                          : ''}
                      </div>
                    </Row>
                    <div className="body">{n.body}</div>
                  </Item>
                ))}
              </Items>
            )}

            <PanelFooter>
              <SubTitle>
                {unreadCount > 0
                  ? `${unreadCount} nueva${unreadCount === 1 ? '' : 's'}`
                  : 'Estás al día'}
              </SubTitle>
            </PanelFooter>
          </Panel>
        </>
      )}
    </Popover>
  )
}

export default HeaderNotification
